import { escapeCurrencyDollars } from './escapeCurrencyDollars';
import { restoreMangledLatexEscapes } from './restoreMangledLatexEscapes';
import { unwrapProseWrappedInDollars } from './unwrapProseWrappedInDollars';
import { unescapeLiteralSquareBrackets } from './unescapeLiteralSquareBrackets';
import { boldMarkdownToHtml } from './boldMarkdownToHtml';
import { escapeDollarsThatCannotDelimit } from './escapeDollarsThatCannotDelimit';
import { markdownTablesToHtml } from './markdownTablesToHtml';
import {
  isHtmlBlob,
  LATEX_CONTROL,
  LATEX_TEXT_MODIFIERS_ENABLED,
  sanitizeTsaProseMath,
} from './sanitizeTsaProseMath';

export { LATEX_TEXT_MODIFIERS_ENABLED };

/** Escape a literal `$` that MathJax would otherwise mistake for an inline-math
 * delimiter (e.g. a stray currency sign or bare `$` in question/option text
 * that isn't part of any LaTeX expression). MathJax's `$...$` scanner pairs up
 * `$` characters left-to-right, so an odd, unpaired `$` either renders as a raw
 * character on its own or swallows unrelated text up to the next `$` in the
 * block. `\$` is MathJax's own escape and renders as a literal dollar sign. */
export const escapeUnpairedDollarSigns = (text) => {
  if (!text.includes('$')) return text;
  const singleDollarPositions = [];
  for (let i = 0; i < text.length; i += 1) {
    if (text[i] !== '$') continue;
    if (text[i - 1] === '\\') continue;
    if (text[i + 1] === '$') { i += 1; continue; }
    singleDollarPositions.push(i);
  }
  if (singleDollarPositions.length % 2 === 0) return text;
  const strayIndex = singleDollarPositions[singleDollarPositions.length - 1];
  return `${text.slice(0, strayIndex)}\\$${text.slice(strayIndex + 1)}`;
};

export const ensureLatexDelimiters = (text) => {
  if (!text || typeof text !== 'string') return text || '';
  if (!LATEX_TEXT_MODIFIERS_ENABLED) return text;
  const hasDelimiters = text.includes('$') || text.includes('\\(') || text.includes('\\[') || text.includes('\\begin{');
  if (hasDelimiters) {
    return escapeUnpairedDollarSigns(text);
  }
  if (isHtmlBlob(text)) {
    return text;
  }
  const hasLatexCommand = LATEX_CONTROL.test(text);
  if (hasLatexCommand) {
    return `$${text.trim()}$`;
  }
  return text;
};

/** Prep exam HTML: unwrap non-math `$`, GFM pipe tables → <table>, delimiter repair.
 * options.tsaSitting is ignored — sanitise is always by content. */
export const prepareExamHtml = (text, options = {}) => {
  if (!text || typeof text !== 'string') return text || '';
  void options;
  // Runs whatever LATEX_TEXT_MODIFIERS_ENABLED is set to: currency amounts are
  // not maths in either mode, and pairing them smashes the prose between.
  // Restore first: a recovered \times or \frac tells escapeCurrencyDollars the
  // block is maths, so it leaves those dollars alone.
  const safe = boldMarkdownToHtml(
    unescapeLiteralSquareBrackets(
      escapeDollarsThatCannotDelimit(escapeCurrencyDollars(
        unwrapProseWrappedInDollars(restoreMangledLatexEscapes(text))
      ))
    )
  );
  if (!LATEX_TEXT_MODIFIERS_ENABLED) {
    return markdownTablesToHtml(safe);
  }
  const source = sanitizeTsaProseMath(safe);
  return ensureLatexDelimiters(markdownTablesToHtml(source));
};
